import type { NarrowByTypeName } from "@/types/narrow-by-typename";

/**
 * Union型Tから、__typenameがUのものだけを抽出する型
 */
type ExtractByTypename<T, U extends string> = NarrowByTypeName<T, U>;

/**
 * オブジェクトが特定の`__typename`を持つかどうかを判定する型ガード関数。
 *
 * @example
 * ```typescript
 * if (isType(data.node, "User")) {
 *   // この中では data.node は User 型として扱われる
 *   console.log(data.node.name);
 * }
 * ```
 *
 * @template T - `__typename`プロパティを持つ可能性のある型
 * @param obj - 判定対象のオブジェクト
 * @param typename - 期待する`__typename`の値
 * @returns 型ガードの結果
 */
export function isType<T extends { __typename?: string }, U extends string>(
  obj: T | null | undefined,
  typename: U,
): obj is ExtractByTypename<T, U> & { __typename: U } {
  return !!obj && obj.__typename === typename;
}

/**
 * オブジェクトから特定の`__typename`を持つ値を抽出する関数。
 * 指定された`__typename`を持つ場合はその値を、持たない場合は`null`を返す。
 *
 * @example
 * ```typescript
 * // Union型からの抽出
 * const user = extractByTypename(data.node, "User");
 * if (user) {
 *   // この中では user は User 型として扱われる
 *   console.log(user.name);
 * }
 *
 * // GraphQL結果からの抽出
 * const project = extractByTypename(data?.project, "SearchAnalyticsProject");
 * ```
 *
 * @template T - `__typename`プロパティを持つ可能性のある型
 * @param obj - 抽出対象のオブジェクト
 * @param typename - 期待する`__typename`の値
 * @returns 指定された`__typename`を持つ値、または`null`
 */
export function extractByTypename<
  T extends { __typename?: string } | null | undefined,
  U extends string,
>(
  obj: T | null | undefined,
  typename: U,
): (ExtractByTypename<T, U> & { __typename: U }) | null {
  return obj && obj.__typename === typename
    ? (obj as ExtractByTypename<T, U> & { __typename: U })
    : null;
}
